/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getPagesViewYPos, getThumbnailsViewYPos } from "./pagedViewState";
import * as constants from "constants/constants";
import { getPageHeight } from "selectors/pageSelectors";
import { getTotalPages, isSinglePageMode, isFullScreenOrFitOnePageMode } from "selectors/documentSelectors";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import {
  getViewPortHeight,
  getDocumentVisibleHeight,
  getViewPortHeightWithoutPagesViewScrollbar
} from "selectors/documentViewSelectors";
import { getCurrentPageIndex } from "selectors/pageNumber";
import {
  getThumbnailNumberHeight,
  getThumbnailBottomMargin,
  getThumbnailBorderSize,
  getDefaultThumbnailHeight,
  getThumbnailMargin,
  getThumbnailViewTopPadding,
  getThumbnailsTopPadding,
  getThumbnailNumTopMargin,
  getThumbnailPadding,
  getThumbnailsHeaderHeight,
  getThumbnailsMinSpaceBetween
} from "thumbnails/ThumbnailService";
import { getViewPortWidth } from "selectors/documentViewSelectors";
import { getColumnMaxWidth, isTwoColumnViewMode, getPagesInView } from "selectors/documentSelectors";
import { getThumbnailColumnCount } from "selectors/thumbnailSelectors";
import { getCurrentThumbnailYPositionFromTop } from "utils/ThumbnailUtils";

export const getPageHeightWithMargin = (stores, i) => getPageHeight(stores, i) + constants.PAGE_BOTTOM_MARGIN;

export const getTotalHeightOfThumbnail = stores =>
  getDefaultThumbnailHeight(stores) +
  getThumbnailNumberHeight(stores) +
  getThumbnailBottomMargin(stores) +
  2 * getThumbnailBorderSize(stores) +
  getThumbnailViewTopPadding(stores) +
  2 * getThumbnailMargin(stores) +
  getThumbnailNumTopMargin(stores) +
  getThumbnailsMinSpaceBetween(stores) +
  2 * getThumbnailPadding(stores);

const getThumbnailsViewHeight = stores =>
  getThumbnailsTopPadding(stores) + getTotalPages(stores) * getTotalHeightOfThumbnail(stores);

const itemsInView = (stores, currY, getItemHeight, padding, shouldConsiderScrollbar = false, viewPortMargin = 0) => {
  let viewPortHeight = !shouldConsiderScrollbar
    ? getViewPortHeight(stores)
    : getViewPortHeightWithoutPagesViewScrollbar(stores);
  if (getIsInPlaceMode(stores)) {
    viewPortHeight = getDocumentVisibleHeight(stores);
  }
  viewPortHeight -= viewPortMargin;

  if (currY < padding) {
    viewPortHeight -= padding - currY;
  }
  if (currY >= padding) {
    currY -= padding;
  } else {
    currY = padding;
  }
  let i = 0;
  const totalPages = getTotalPages(stores);

  while (i < totalPages && currY >= 0) {
    currY -= getItemHeight(stores, i);
    i++;
  }

  let firstPageInView = i - 1;
  let partOfTopPageInView = -currY;

  viewPortHeight -= partOfTopPageInView;

  while (i < totalPages && viewPortHeight > 0) {
    viewPortHeight -= getItemHeight(stores, i);
    i++;
  }
  let lastPageInView = i - 1;
  return {
    start: firstPageInView,
    end: lastPageInView
  };
};

const itemsInViewForThumbnail = (
  stores,
  currY,
  getItemHeight,
  padding,
  shouldConsiderScrollbar = false,
  viewPortMargin = 0
) => {
  const thumbnailColumnCount = getThumbnailColumnCount(stores);
  let viewPortHeight = !shouldConsiderScrollbar
    ? getViewPortHeight(stores)
    : getViewPortHeightWithoutPagesViewScrollbar(stores);
  if (getIsInPlaceMode(stores)) {
    viewPortHeight = getDocumentVisibleHeight(stores);
  }
  viewPortHeight -= viewPortMargin;

  if (currY < padding) {
    viewPortHeight -= padding - currY;
  }
  if (currY >= padding) {
    currY -= padding;
  } else {
    currY = padding;
  }
  let i = 0;
  const totalPages = getTotalPages(stores);

  while (i < totalPages && currY >= 0) {
    currY -= getItemHeight(stores, i);
    i += thumbnailColumnCount;
  }
  // Increasing i by thumbnailColumnCount, so that we can move from one row to the next
  // Setting first index to be the first thumbnail in the row which is visible in the panel.
  let firstPageInView = Math.max(0, i - thumbnailColumnCount);
  let partOfTopPageInView = -currY;

  viewPortHeight -= partOfTopPageInView;
  while (i < totalPages && viewPortHeight - getItemHeight(stores, i) > 0) {
    viewPortHeight -= getItemHeight(stores, i);
    i += thumbnailColumnCount;
  }
  // Setting last index to be the last thumbnail in the row which is visible in the panel.
  let lastPageInView = Math.min(i + thumbnailColumnCount - 1, totalPages - 1);
  return {
    start: firstPageInView,
    end: lastPageInView
  };
};

export const pagesInView = stores => {
  if (isSinglePageMode(stores) || isFullScreenOrFitOnePageMode(stores)) {
    return {
      start: getCurrentPageIndex(stores),
      end: getCurrentPageIndex(stores)
    };
  }
  // Two column is not handled here, fallback to getPagesInView
  if (isTwoColumnViewMode(stores)) return getPagesInView(stores);
  return itemsInView(stores, getPagesViewYPos(stores), getPageHeightWithMargin, constants.PAGES_TOP_PADDING, true);
};

export const thumbnailsInView = stores =>
  itemsInViewForThumbnail(
    stores,
    getThumbnailsViewYPos(stores),
    getTotalHeightOfThumbnail,
    getThumbnailsTopPadding(stores),
    false,
    getThumbnailsHeaderHeight(stores)
  );

export const isHorizontallyScrollable = stores => {
  const columnMaxWidth = getColumnMaxWidth(stores);
  const viewPortWidth = getViewPortWidth(stores);
  let rowWidth = columnMaxWidth;
  if (isTwoColumnViewMode(stores)) {
    rowWidth = 2 * columnMaxWidth - constants.TWO_COLUMN_MARGIN;
  }
  return rowWidth > viewPortWidth;
};

export const isVeritcallyScrollable = stores =>
  getPageHeight(stores, getCurrentPageIndex(stores)) > getViewPortHeightWithoutPagesViewScrollbar(stores);

export const getThumbnailDisplayData = stores => {
  let currThumbnailYStartPos = getCurrentThumbnailYPositionFromTop(stores, getTotalHeightOfThumbnail(stores));
  let currThumbnailYEndPos = currThumbnailYStartPos + getTotalHeightOfThumbnail(stores);
  let thumbnailsViewYStartPos = getThumbnailsViewYPos(stores);
  let thumbnailsViewYEndPos = thumbnailsViewYStartPos + getViewPortHeight(stores);
  let isCurrThumbnailVisible = false;
  let newThumbnailsViewYPos = currThumbnailYStartPos;
  if (currThumbnailYStartPos >= thumbnailsViewYStartPos && currThumbnailYEndPos <= thumbnailsViewYEndPos) {
    isCurrThumbnailVisible = true;
  } else {
    isCurrThumbnailVisible = false;
    if (currThumbnailYEndPos > thumbnailsViewYEndPos) {
      newThumbnailsViewYPos = currThumbnailYStartPos - (getViewPortHeight(stores) - getTotalHeightOfThumbnail(stores));
    }
  }
  return {
    isCurrThumbnailVisible,
    newThumbnailsViewYPos
  };
};

export const getNextThumbnailsInViewYPos = stores => {
  let yPos = getThumbnailsViewYPos(stores) + getViewPortHeight(stores);
  let totalThumbnailsViewHeight = getThumbnailsViewHeight(stores);
  if (yPos > totalThumbnailsViewHeight) {
    yPos = totalThumbnailsViewHeight;
  }
  return yPos;
};

export const getPrevThumbnailsInViewYPos = stores => {
  let yPos = getThumbnailsViewYPos(stores) - getViewPortHeight(stores);
  if (yPos < 0) {
    yPos = 0;
  }
  return yPos;
};

export const getLastThumbnailsInViewYPos = stores => getThumbnailsViewHeight(stores) - getViewPortHeight(stores);

export const getTileCanvasRef = stores => stores.pagedViewState.tileCanvasRef;

export const getTileCanvasRefPerPage = (stores, pageIndex) => stores.pagedViewState.tileCanvasRef.get(pageIndex);

export const getIsTileCanvasLoaded = stores => stores.pagedViewState.isTileCanvasLoaded;
